import React, { useState } from 'react';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import CurrencyInput from 'components/base/currency-input';
import { useNavigate } from 'react-router-dom';
import { UpdateKitchenProcurementType } from 'models/procurement.model';
import useKitchenProcurementContext from 'contexts/kitchen/procurement.context/context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const KitchenUpdateProcurementDetails = () => {
  const { procurementToUpdate, setProcurementToUpdate } = useKitchenProcurementContext();
  const [isUpdatingProcurement, setIsUpdatingProcurement] = useState(false);
  const router = useNavigate();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.KitchenRequisition, RolePermission.Write);

  const updateProcurement = async () => {
    if (!canWriteProcurement) return;
    const data = {
      items: procurementToUpdate.items
        .map((procurement_detail) => {
          return {
            ingredient_id: procurement_detail.ingredient_id,
            quantity_received: Number(procurement_detail.quantity_received) || 0
          };
        })
        .filter((item) => item.quantity_received > 0)
    };
    try {
      setIsUpdatingProcurement(true);
      const response = await apiService.post(
        `kitchen-store/kitchen-request/${procurementToUpdate.id}/confirm-items-received`,
        { data }
      );
      if (response) {
        toast(
          <ToastMessage
            message='Internal procurement request updated successfully'
            type='success'
          />
        );
        router(-1);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating internal procurement request'}
          type='error'
        />
      );
    } finally {
      setIsUpdatingProcurement(false);
    }
  };

  const setValue = (index: number, field: keyof UpdateKitchenProcurementType, value: string) => {
    setProcurementToUpdate((prev) => ({
      ...prev,
      items: prev.items.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    }));
  };

  return (
    <RequestDetailsWrapper
      codeId={procurementToUpdate.code}
      statusTag={<ProcurementStatusTag type={procurementToUpdate.status} />}
      header='Update request'
      subHeader='State the quantity received in good condition where necessary'>
      {procurementToUpdate.items.map((item, index) => (
        <div className='w-full flex py-[24px] px-[28px] border-b border-b-[#F7F7F8]' key={index}>
          <div className='w-full grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
            <Input
              design='bordered'
              label='Item Name'
              name='name'
              placeholder='Item Name'
              value={item.ingredient.name}
              disabled
            />
            <CurrencyInput
              label='Quantity requested'
              placeholder='Quantity requested'
              name='quantity_requested'
              value={item.quantity}
              hasUnit
              unitValue={item.ingredient.unit_type}
              disableUnit
              hideCurrency
              explicitRender
              onWheel={(e) => e.currentTarget.blur()}
              onChange={() => {
                return;
              }}
              disabled
            />
            <CurrencyInput
              label='Quantity sent'
              placeholder='Quantity sent'
              name='quantity_sent'
              hasUnit
              unitValue={item.ingredient.unit_type}
              disableUnit
              hideCurrency
              explicitRender
              value={(item as any).quantity_sent}
              onWheel={(e) => e.currentTarget.blur()}
              onChange={() => {
                return;
              }}
              disabled
            />
            <CurrencyInput
              label='Quantity received'
              placeholder='Quantity received'
              name='quantity_sent'
              hasUnit
              unitValue={item.ingredient.unit_type}
              disableUnit
              hideCurrency
              explicitRender
              value={(item as any).quantity_received}
              disabled={!canWriteProcurement || Number(item.quantity_sent) === 0}
              onWheel={(e) => e.currentTarget.blur()}
              onChange={(value) => {
                Number(value) <= Number(item.quantity_sent) &&
                  setValue(index, 'quantity_received', value);
              }}
            />
          </div>
        </div>
      ))}
      <div>
        <hr className='border-t-4 border-[#F7F7F8]' />
        <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
          <Button variant='link' color='gray' small onClick={() => router(-1)}>
            Back
          </Button>
          {canWriteProcurement && (
            <Button
              variant='block'
              color='primary'
              small
              className='w-[153px] h-[43px]'
              onClick={() => updateProcurement()}
              loading={isUpdatingProcurement}>
              Confirm
            </Button>
          )}
        </div>
      </div>
    </RequestDetailsWrapper>
  );
};

export default KitchenUpdateProcurementDetails;
